import React, { Component } from 'react';
import { Collapse, Container, Navbar, NavbarBrand, NavItem, NavLink } from 'reactstrap';
import { Link, NavLink as RouterNavLink, withRouter } from 'react-router-dom';
import './NavMenu.css';
import { isSessionActive } from '../shared/auth';

const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: 'home', exact: true },
  { to: '/retirementsimulator', label: 'Simulator', icon: 'simulator' },
  { to: '/exampleone', label: 'Examples', icon: 'examples' },
  { to: '/about', label: 'About', icon: 'about' },
  { to: '/analytics', label: 'Analytics', icon: 'analytics', requiresSession: true },
  { to: '/login', label: 'Login', icon: 'login' },
];

function NavMenuIcon({ name }) {
  switch (name) {
    case 'home':
      return (
        <svg viewBox="0 0 24 24" className="nav-menu-icon" aria-hidden="true">
          <path d="M3.5 10.8L12 4l8.5 6.8" />
          <path d="M6.5 9.7V20h11V9.7" />
          <path d="M10 20v-5.7h4V20" />
        </svg>
      );
    case 'simulator':
      return (
        <svg viewBox="0 0 24 24" className="nav-menu-icon" aria-hidden="true">
          <path d="M4 4.5V20h16" />
          <path d="M7.2 15.5l3.4-3.7 3 2.1 4.2-6.1" />
          <path d="M8 19.8v-3.2" />
          <path d="M12 19.8v-5.1" />
          <path d="M16 19.8v-3.7" />
        </svg>
      );
    case 'examples':
      return (
        <svg viewBox="0 0 24 24" className="nav-menu-icon" aria-hidden="true">
          <path d="M9 14.8c-1.8-1.1-3-3.1-3-5.4A6 6 0 0 1 18 9.4c0 2.3-1.2 4.3-3 5.4" />
          <path d="M10 17.2h4" />
          <path d="M9.4 20h5.2" />
          <path d="M10 14.8h4v2.1h-4z" />
        </svg>
      );
    case 'about':
      return (
        <svg viewBox="0 0 24 24" className="nav-menu-icon" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 10v6" />
          <path d="M12 7.2h.01" />
        </svg>
      );
    case 'login':
      return (
        <svg viewBox="0 0 24 24" className="nav-menu-icon" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="9" r="2.6" />
          <path d="M7.6 17.2c1.2-2.1 2.8-3.2 4.4-3.2s3.2 1.1 4.4 3.2" />
        </svg>
      );
    case 'analytics':
      return (
        <svg viewBox="0 0 24 24" className="nav-menu-icon" aria-hidden="true">
          <path d="M4 20V10" />
          <path d="M10 20V4" />
          <path d="M16 20v-7" />
          <path d="M22 20H2" />
        </svg>
      );
    default:
      return null;
  }
}

class NavMenuBase extends Component {
  static displayName = NavMenuBase.name;

  constructor (props) {
    super(props);

    this.toggleNavbar = this.toggleNavbar.bind(this);
    this.handleDocumentPointer = this.handleDocumentPointer.bind(this);
    this.handleNavItemClick = this.handleNavItemClick.bind(this);
    this.closeNavbar = this.closeNavbar.bind(this);
    this.headerRef = React.createRef();
    this.state = {
      collapsed: true
    };
  }

  componentDidMount () {
    document.addEventListener('mousedown', this.handleDocumentPointer);
    document.addEventListener('touchstart', this.handleDocumentPointer);
  }

  componentDidUpdate (prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname && !this.state.collapsed) {
      this.closeNavbar();
    }
  }

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleDocumentPointer);
    document.removeEventListener('touchstart', this.handleDocumentPointer);
  }

  toggleNavbar () {
    this.setState({
      collapsed: !this.state.collapsed
    });
  }

  handleDocumentPointer (event) {
    if (this.state.collapsed || !this.headerRef.current) {
      return;
    }

    if (!this.headerRef.current.contains(event.target)) {
      this.closeNavbar();
    }
  }

  handleNavItemClick () {
    this.closeNavbar();
  }

  closeNavbar () {
    this.setState({
      collapsed: true
    });
  }

  render () {
    return (
      <header ref={this.headerRef}>
        <Navbar className="site-navbar navbar-expand-sm navbar-toggleable-sm ng-white border-bottom box-shadow mb-3" light>
          <Container className="site-navbar-container">
            <NavbarBrand tag={Link} to="/" className="site-navbar-brand">
              <span className="site-navbar-brand-mark" aria-hidden="true">
                <svg viewBox="0 0 24 24" className="site-navbar-brand-icon">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M7 15.8V12.6" />
                  <path d="M11 15.8V9.8" />
                  <path d="M15 15.8v-4.1" />
                  <path d="M7 8.6l3.8-2.4 3.1 2 3.1-3" />
                </svg>
              </span>
              <span className="site-navbar-brand-text">ASD Retirement</span>
            </NavbarBrand>
            <button
              type="button"
              aria-label="Toggle navigation"
              onClick={this.toggleNavbar}
              className={`site-navbar-toggle ${!this.state.collapsed ? 'is-open' : ''}`}
            >
              <span className="site-navbar-toggle-lines" aria-hidden="true">
                {this.state.collapsed ? '☰' : '✕'}
              </span>
            </button>
            <Collapse className="site-navbar-collapse d-sm-inline-flex flex-sm-row-reverse" isOpen={!this.state.collapsed} navbar>
              <ul className="navbar-nav site-navbar-nav">
                {NAV_ITEMS.filter(item => !item.requiresSession || isSessionActive()).map((item) => (
                  <NavItem className="site-nav-item" key={item.to}>
                    <NavLink
                      tag={RouterNavLink}
                      exact={item.exact}
                      activeClassName="is-active"
                      className="site-nav-link"
                      onClick={this.handleNavItemClick}
                      to={item.to}
                    >
                      <span className="nav-menu-icon-wrap">
                        <NavMenuIcon name={item.icon} />
                      </span>
                      <span className="site-nav-label">{item.label}</span>
                    </NavLink>
                  </NavItem>
                ))}
              </ul>
            </Collapse>
          </Container>
        </Navbar>
      </header>
    );
  }
}

export const NavMenu = withRouter(NavMenuBase);
